import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import AuthLayout from '../components/auth/AuthLayout';
import AuthResult from '../components/auth/AuthResult';
import PasswordRequirements from '../components/auth/PasswordRequirements';
import { isPasswordValid as checkPassword } from '../components/auth/passwordRules';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import { buttonClassName } from '../components/ui/buttonStyles';
import { FormField, Input } from '../components/ui/Field';
import { notifySuccess } from '../utils/notify';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token');

  const isPasswordValid = checkPassword(password);

  if (!token) {
    return (
      <AuthLayout>
        <AuthResult
          tone="danger"
          title="Link Inválido"
          actions={(
            <Link to="/forgot-password" className={buttonClassName({ fullWidth: true })}>
              Solicitar Novo Link
            </Link>
          )}
        >
          <p>O link de redefinição de senha é inválido ou expirou.</p>
        </AuthResult>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (!isPasswordValid) {
      setError('A senha não atende aos requisitos mínimos de segurança');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/reset-password', {
        token,
        newPassword: password
      });

      // Show success message
      notifySuccess('Senha redefinida com sucesso! Você será redirecionado para o login.');
      navigate('/login', {
        state: { message: 'Senha redefinida com sucesso! Faça login com sua nova senha.' }
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erro ao redefinir senha. Tente novamente.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  return (
    <AuthLayout title="Redefinir Senha" subtitle="Digite sua nova senha">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert variant="danger">{error}</Alert>}

        <FormField htmlFor="password" label="Nova Senha">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={loading}
          />
        </FormField>
        {password && <PasswordRequirements password={password} />}

        <FormField
          htmlFor="confirmPassword"
          label="Confirmar Nova Senha"
          error={passwordsMismatch ? 'As senhas não coincidem' : undefined}
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            invalid={passwordsMismatch}
            disabled={loading}
          />
        </FormField>

        <Button
          type="submit"
          fullWidth
          loading={loading}
          disabled={!isPasswordValid || password !== confirmPassword}
        >
          {loading ? 'Redefinindo...' : 'Redefinir Senha'}
        </Button>

        <div className="text-center">
          <Link to="/login" className="text-sm text-link transition hover:text-link-hover">
            Voltar ao Login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
